import { type GetTipiDomandaParams } from '@framework/api-client/api/inquiries/schemas/getTipiDomandaParams.ts';
import { GetTipiDomandaStato } from '@framework/api-client/api/inquiries/schemas/getTipiDomandaStato.ts';
import { useGetFrontTipiDomanda } from '@framework/api-client/api/inquiries-compile/tipi-domanda/tipi-domanda.ts';
import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { StyledForm } from '@framework/form/form/StyledForm.tsx';
import { FlexSubmissionRow } from '@framework/frontoffice-submission/submission/FlexSubmissionRow.tsx';
import { LoaderSpin } from '@framework/ui/components/loader/LoaderSpin.tsx';
import { MessageFilteredList } from '@framework/ui/components/messages/MessageFilteredList.tsx';
import { AlignRow } from '@framework/ui/components/row/AlignRow.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { ColumnSortDirections } from '@framework/ui/components/table/TableColumnSort.tsx';
import { getPagination } from '@framework/ui/core/pagination/getPagination.ts';
import { usePaginationUrlQueryState } from '@framework/ui/core/pagination/paginationUrlQuery.ts';
import { useBreadcrumbItem } from '@framework/ui/core/router/breadcrumbs/BreadcrumbContext.tsx';
import {
  type BrandedPath,
  useHistory,
} from '@framework/ui/core/router/TypedRoute.tsx';
import { Card, Col, Empty, Pagination, Row } from 'antd';
import Title from 'antd/es/typography/Title';
import { isEqual, omit } from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type RouteComponentProps } from 'react-router';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import styled from 'styled-components';
import { FrontPageTitleContainer } from '#theme';
import { FilterDrawerButton } from '../../../ui/drawer/FilterDrawerButton.tsx';
import { PageLayout } from '../../../ui/layout/PageLayout.tsx';
import { NetworkAlertPage } from '../../errors/network-alert/NetworkAlertPage.tsx';
import { LoadingPage } from '../../loading/LoadingPage.tsx';
import { InquiryFilterForm } from './filter/InquiryFilterForm.tsx';
import { InquiryFilterFormFields } from './filter/InquiryFilterFormFields.tsx';
import { InquiryFilterFormFieldsLogic } from './filter/InquiryFilterFormFieldsLogic.ts';
import { InquiryFilterRemoveButton } from './filter/InquiryFilterRemoveButton.tsx';
import {
  InquiryListSchema,
  type InquirySchemaType,
} from './InquiryListSchema.ts';
import { InquiryLinkButton } from './item/InquiryLinkButton.tsx';
import { InquiryListItem } from './item/InquiryListItem.tsx';

const MaxWidthContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  ${down('sm')} {
    padding: 0 12px;
  }
`;

const PAGE_TITLE_ID = 'inquiry-list-title-focus-id';
const returnFocusToPageTitle = () => {
  const titleElement = document.getElementById(PAGE_TITLE_ID);
  titleElement?.focus();
};

export interface IInquiryListPageProps extends RouteComponentProps {}

const defaultQuery = getPagination({
  page: 0,
  pageSize: 10,
  sort: 'dataCreazione',
  direction: ColumnSortDirections.descend,
});

export function InquiryListPage(props: IInquiryListPageProps) {
  const history = useHistory();
  const { t } = useTranslation();

  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
  const isMobile = useBreakpoint(down('sm'));

  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: t('inquiry.lista.briciole.domande', 'Domande'),
  });

  const [query, setQuery] = usePaginationUrlQueryState<GetTipiDomandaParams>({
    history,
    input: defaultQuery,
  });

  const { data, isPreviousData, error, isLoading, isFetching } =
    useGetFrontTipiDomanda(
      { ...query, osservazioni: false },
      {
        query: { keepPreviousData: true },
      },
    );

  const { dati: inquiries, meta } = useMemo(() => data ?? {}, [data]);

  const isFiltered = useMemo(() => {
    if (isFetching) return false;
    return !isEqual(
      omit(query, ['pagina', 'ordinamenti', 'limite']),
      omit(defaultQuery, ['pagina', 'ordinamenti', 'limite']),
    );
  }, [query, isFetching]);

  const initialValues = useMemo(() => {
    return {
      ...query,
      soloAttive: query.stato === GetTipiDomandaStato.APERTA,
    };
  }, [query]);

  if (isLoading) return <LoadingPage />;

  if (error) {
    return (
      <NetworkAlertPage
        message={t(
          'inquiry.lista.errori.impossibile-caricare-la-sezione',
          "Impossibile caricare l'elenco delle domande",
        )}
        error={error}
      />
    );
  }

  return (
    <FormHandler
      initialValues={initialValues}
      schema={InquiryListSchema}
      // disableReinitialize
      hideVisualFeedback
      onSubmit={async (rawValues: InquirySchemaType) => {
        try {
          const values = await InquiryListSchema.validate(rawValues);
          const formFilter =
            InquiryFilterFormFieldsLogic.showStateSelectFilter()
              ? values
              : {
                  ...omit(values, 'soloAttive'),
                  // Se il checkbox è selezionato, allora il valore è APERTA
                  stato: values.soloAttive
                    ? GetTipiDomandaStato.APERTA
                    : undefined,
                };

          setQuery({
            ...query,
            ...formFilter,
            // reimposto alla prima pagina con la ricerca
            pagina: 0,
          });
          setOpenFilterDrawer(false);
        } catch (e) {
          console.error(`[Errore] Errore nella ricerca`, e);
        }
      }}
      layout="vertical"
    >
      <PageLayout
        extraButtons={
          isMobile && (
            <>
              <FilterDrawerButton
                showFormButtons
                isFiltered={isFiltered}
                open={openFilterDrawer}
                onOpen={() => setOpenFilterDrawer(true)}
                onClose={() => setOpenFilterDrawer(false)}
                onClear={() => {
                  setQuery({});
                  setOpenFilterDrawer(false);
                }}
              >
                <StyledForm component="div">
                  <InquiryFilterFormFields />
                </StyledForm>
              </FilterDrawerButton>
            </>
          )
        }
      >
        <FlexSubmissionRow
          menu={
            !isMobile && (
              <InquiryFilterForm
                buttons={
                  isFiltered && (
                    <InquiryFilterRemoveButton
                      onClick={() => {
                        setQuery({});
                        returnFocusToPageTitle();
                      }}
                    />
                  )
                }
              />
            )
          }
        >
          <LoaderSpin spinning={isFetching} noPadding hasFullWidthChildren>
            <MaxWidthContainer>
              <FrontPageTitleContainer>
                <Title level={1} tabIndex={-1} id={PAGE_TITLE_ID}>
                  {t('inquiry.tutte-le-domande', 'Tutte le Domande')}
                </Title>
              </FrontPageTitleContainer>
              <MessageFilteredList
                style={{ textAlign: isMobile ? 'center' : 'left' }}
                visible={isFiltered}
                items={meta?.totale ?? null}
                message={t(
                  'inquiry.lista.filtri-applicati.domande-mostrate-in-base-ai-filtri-di-ricerca',
                  'domande trovate in base ai filtri di ricerca.',
                )}
                action={
                  <InquiryFilterRemoveButton
                    size="small"
                    onClick={() => {
                      setQuery({});
                      returnFocusToPageTitle();
                    }}
                  />
                }
              />

              <Row gutter={[24, 24]}>
                {inquiries?.length === 0 && (
                  <Col span={24}>
                    <Card>
                      <Empty
                        description={t(
                          'inquiry.lista.vuota.nessuna-domanda-da-mostrare',
                          'Nessuna domanda da mostrare.',
                        )}
                      />
                      <VerticalSpace size={8} />
                      <AlignRow justify="center">
                        <InquiryFilterRemoveButton
                          onClick={() => {
                            setQuery({});
                            returnFocusToPageTitle();
                          }}
                        />
                      </AlignRow>
                    </Card>
                  </Col>
                )}

                {inquiries?.map(inquiry => (
                  <Col xs={24} sm={24} md={12} key={inquiry.id}>
                    <InquiryListItem
                      inquiry={inquiry}
                      actions={<InquiryLinkButton inquiry={inquiry} />}
                    />
                  </Col>
                ))}
              </Row>
              <div>
                <VerticalSpace />
                <Pagination
                  current={(meta?.pagina ?? 0) + 1}
                  total={meta?.totale ?? 0}
                  pageSize={meta?.limite ?? 10}
                  showSizeChanger
                  onChange={(page, pageSize) => {
                    setQuery({ ...query, pagina: page - 1, limite: pageSize });
                  }}
                />
                {/* accessibilità inserisco lo spazio sotto per non far tagliare l'outline */}
                <VerticalSpace size={6} />
              </div>
            </MaxWidthContainer>
          </LoaderSpin>
        </FlexSubmissionRow>
      </PageLayout>
    </FormHandler>
  );
}
